import { Routes, Route, Navigate } from "react-router-dom"
import { Suspense } from "react"
import { lazy } from "react"
import { useAuth } from "./presentation/hooks/useAuth"
import { redirectByRole } from "./presentation/utils/helpers"
import { UserRole } from "./domain/enums/roles"
import PrivateRoute from "./presentation/components/auth/PrivateRoute"
import Loading from "./presentation/components/ui/loading"

// Lazy loaded components
const DashboardLayout = lazy(() => import("./presentation/layouts/DashboardLayout"))
const LoginPage = lazy(() => import("./presentation/pages/auth/Login"))
const ResetPasswordPage = lazy(() => import("./presentation/pages/auth/ResetPassword"))
const AdminRoutes = lazy(() => import("./presentation/routes/AdminRoutes"))
const UserRoutes = lazy(() => import("./presentation/routes/UserRoutes"))
const EvaluatorRoutes = lazy(() => import("./presentation/routes/EvaluatorRoutes"))
const PublicRoutes = lazy(() => import("./presentation/routes/PublicRoutes"))

const LoadingSpinner = () => <Loading />

// Layout with Loading for Dashboard routes
const DashboardWithSuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner />}>
    <DashboardLayout>
      {children}
    </DashboardLayout>
  </Suspense>
)

function App() {
  const { user, isLoading } = useAuth()
  // Show loading while auth is initializing
  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/*" element={<PublicRoutes />} />

          {/* Reset Password Route - Requires authentication but allows access */}
          <Route
            path="/reset-password"
            element={
              <PrivateRoute allowedRoles={[UserRole.USER, UserRole.ADMIN, UserRole.EVALUATOR]}>
                <ResetPasswordPage />
              </PrivateRoute>
            }
          />

          {/* Protected Dashboard Routes */}
          <Route
            path="/admin/*"
            element={
              <PrivateRoute allowedRoles={[UserRole.ADMIN]}>
                <DashboardWithSuspense>
                  <AdminRoutes />
                </DashboardWithSuspense>
              </PrivateRoute>
            }
          />

          <Route
            path="/user/*"
            element={
              <PrivateRoute allowedRoles={[UserRole.USER]}>
                <UserRoutes />
              </PrivateRoute>
            }
          />

          <Route
            path="/evaluator/*"
            element={
              <PrivateRoute allowedRoles={[UserRole.EVALUATOR]}>
                <DashboardWithSuspense>
                  <EvaluatorRoutes />
                </DashboardWithSuspense>
              </PrivateRoute>
            }
          />

          {/* Default redirect based on role */}
          <Route
            path="/"
            element={
              user ? (
                <Navigate to={redirectByRole(user.role as UserRole)} replace />
              ) : (
                <Suspense fallback={<LoadingSpinner />}>
                  <LoginPage />
                </Suspense>
              )
            }
          />
        </Routes>
      </Suspense>
    </div>
  )
}

export default App